import maplibregl from 'maplibre-gl';
import { PMTiles, Protocol } from 'pmtiles';

// Protocolo pmtiles:// de MapLibre para Kairos, registrado una sola vez (antes lo registraban map-container y map_left
// por separado y ganaba el último importado).
// Las teselas raster que faltan en el archivo (vacías: p. ej. sin convección en toda la tesela) llegan con data null y
// MapLibre no las da nunca por cargadas: el mapa no llega a idle ni a loaded() y, al cambiar de hora, esa tesela se
// queda con la imagen de la hora anterior. Se cambian por una tesela transparente de 1 px
const protocol = new Protocol();

// Archivo entero en un solo GET, como Windy (un objeto HTTP pequeño por frame). Salvo satélite, los frames pmtiles
// pesan 1–100 KB: antes cada hora costaba la cabecera (rango de 16 KB) más un rango por tesela y, en Chrome/Edge de
// Windows, pmtiles las pide con cache: 'no-store'. Ahora es una petición por hora, la caché del navegador sirve y volver
// a una hora ya vista no pide nada. Los *_live/ se sobrescriben con el mismo nombre en cada run: se revalidan con ETag.
// Histórico = cualquier carpeta *_historical* (cnn_025_historical/, rf_historical_v3/, contrail_historical_rf/…): antes
// solo valía '_historical/' y los frames históricos de Contrail se revalidaban todos
const isHistorical = (url) => /_historical/.test(url);

class WholeFileSource {
  constructor(url) {
    this.url = url;
    this.bytes = null;
  }

  getKey() {
    return this.url;
  }

  async getBytes(offset, length) {
    this.bytes ??= fetch(this.url, { cache: isHistorical(this.url) ? 'default' : 'no-cache' }).then((r) => {
      // Mismo texto que pmtiles: los mapas reconocen el 404 ("ese archivo ya no existe") por el mensaje
      if (!r.ok) throw new Error(`Bad response code: ${r.status}`);
      return r.arrayBuffer();
    });
    return { data: (await this.bytes).slice(offset, offset + length) };
  }
}

// Solo los frames de datos (pred_/obs_/ice_… con la hora en el nombre). Satélite (10–12 MB) y los pmtiles fijos, como
// firs.pmtiles (1,9 MB de sectores), siguen con rangos: de ellos solo hacen falta las teselas a la vista
const isSmallFrame = (url) => /\d{8}T\d{6}Z[^/]*\.pmtiles$/.test(url) && !/\/sat_/.test(url);

const ensureWholeFile = (url) => {
  if (protocol.tiles.has(url) || !isSmallFrame(url)) return;
  // ponytail: tope por número de archivos (≤ 100 KB cada uno, ~40 MB como mucho); sale el más antiguo y, si vuelve a
  // hacer falta, se pide otra vez (normalmente desde la caché del navegador)
  if (protocol.tiles.size >= 400) protocol.tiles.delete(protocol.tiles.keys().next().value);
  protocol.add(new PMTiles(new WholeFileSource(url)));
};

// Descarga en segundo plano los archivos de una secuencia entera, como el radar de Windy antes de reproducir: después
// cualquier hora del slider se pinta sin ir a la red. Tres a la vez, para no quitarle ancho de banda a la hora que se ve.
// Se llama al llegar el listado de una capa: los bytes de un *_live/ guardados antes (sobreviven a la navegación de la
// SPA) pueden ser de otro run con el mismo nombre y se pintaban las horas que se solapan con el run anterior. Fuera de la
// memoria: se vuelven a pedir revalidando con ETag (304 si el run no ha cambiado)
export const prefetchPmtiles = (urls) => {
  urls.forEach((url) => {
    if (!isHistorical(url)) protocol.tiles.delete(url);
  });
  const queue = urls.filter(isSmallFrame);
  const next = () => {
    const url = queue.shift();
    if (!url) return;
    ensureWholeFile(url);
    protocol.tiles.get(url).source.getBytes(0, 1).catch(() => protocol.tiles.delete(url)).finally(next);
  };
  next();
  next();
  next();
};

maplibregl.addProtocol('pmtiles', async (params, abortController) => {
  const url = params.url.slice('pmtiles://'.length).replace(/\/\d+\/\d+\/\d+$/, '');
  ensureWholeFile(url);
  try {
    const response = await protocol.tile(params, abortController);
    return response.data === null ? { data: await createImageBitmap(new ImageData(1, 1)) } : response;
  } catch (err) {
    // pmtiles guarda en su caché la promesa rechazada de la cabecera: un 404 o un corte de red puntual dejaba esa hora
    // sin cargar hasta recargar la página. Fuera el archivo de la caché para que el siguiente intento lo pida de nuevo
    if (err.name !== 'AbortError') protocol.tiles.delete(url);
    throw err;
  }
});
